import React, { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Plan, awaitingSetupFee, setupFeeLink, setupFeeText } from '../lib/plans'

/**
 * The one-off setup fee, paid BEFORE the trial starts (user decision 2026-10-24, W9b).
 *
 * deploy_merchant creates a store on a plan with a setup fee as status 'pending_setup'. The fee
 * is paid through the plan's Stripe Payment Link (subscription_plans.setup_fee_url), opened in a
 * new tab so the wizard keeps its state; the Stripe webhook then starts the 7-day trial. FlowStock
 * staff can also waive it (Pro given away on the sales call, Integrate "On request"). This card
 * polls the subscription until that happens and tells the parent whether Go live is allowed.
 */
const SetupFeeCard: React.FC<{
  merchantId: string
  email?: string
  plans: Plan[]
  onAwaitingChange: (awaiting: boolean) => void
}> = ({ merchantId, email, plans, onAwaitingChange }) => {
  const [sub, setSub] = useState<{ status: string; plan_id: string } | null>(null)
  const [linkUrl, setLinkUrl] = useState<string | null>(null)
  const [wasAwaiting, setWasAwaiting] = useState(false)

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('subscriptions').select('status, plan_id').eq('merchant_id', merchantId).maybeSingle()
    setSub((data as any) ?? null)
  }, [merchantId])

  useEffect(() => { load() }, [load])

  const awaiting = awaitingSetupFee(sub)
  useEffect(() => {
    onAwaitingChange(awaiting)
    if (awaiting) setWasAwaiting(true)
  }, [awaiting, onAwaitingChange])

  // While waiting: re-check every 5 s and whenever the tab regains focus (back from Stripe).
  useEffect(() => {
    if (!awaiting) return
    const t = window.setInterval(load, 5000)
    window.addEventListener('focus', load)
    return () => { window.clearInterval(t); window.removeEventListener('focus', load) }
  }, [awaiting, load])

  // Read on its own so a missing column (app shipped before the migration) only hides the button.
  useEffect(() => {
    if (!sub?.plan_id) return
    ;(supabase as any).from('subscription_plans').select('setup_fee_url').eq('id', sub.plan_id).maybeSingle()
      .then(({ data, error }: any) => setLinkUrl(error ? null : data?.setup_fee_url ?? null))
  }, [sub?.plan_id])

  if (!sub) return null
  if (!awaiting) {
    return wasAwaiting ? (
      <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 mb-4 text-sm text-green-300">
        Setup fee received. Your 7-day trial has started.
      </div>
    ) : null
  }

  const plan = plans.find((p) => p.id === sub.plan_id)
  const fee = plan ? setupFeeText(plan) : null
  const href = setupFeeLink(linkUrl, merchantId, email)

  return (
    <div className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-4 mb-4">
      <p className="text-white font-semibold text-sm">One-off setup{fee ? `: ${fee}` : ''}</p>
      <p className="text-white/60 text-sm mt-1">
        Your {plan?.name ?? sub.plan_id} trial starts when the setup fee is paid. Until then nothing syncs to your store.
      </p>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer"
          className="inline-flex mt-3 px-5 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm">
          Pay the setup fee
        </a>
      ) : (
        <p className="text-white/60 text-sm mt-3">
          We send you the setup invoice.{' '}
          {plan?.contact_url && <a className="text-orange-400 underline" href={plan.contact_url}>Contact us</a>}
        </p>
      )}
      <p className="text-white/30 text-xs mt-3">Paid already? This page updates by itself within a few seconds.</p>
    </div>
  )
}

export default SetupFeeCard
